/**
 * MobileCommunityList — Lightweight mobile community list component.
 * Replicates CommunityList mobile-mode UI without heavy desktop overhead.
 * Uses shared hooks for business logic, CSS variables for theming.
 */
import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Heart, MessageSquare, Share2, Bookmark, ChevronDown, TrendingUp, TrendingDown,
  Flame, Clock, Calendar, Hash, AlertCircle, ThumbsDown, MoreVertical, Pin, Trash,
  Globe, Users,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useCommunityData, type Community } from '../shared/hooks/useCommunityData';
import { getCommunities } from '../../lib/api-client';
import { useSidebarSearch } from '../shared/hooks/useSidebarSearch';
import { type FeedPost, type Reply } from '../shared/hooks/useFeedData';
import { getLegacyPostUrl, getCommunityPostUrl, getForumPostUrl } from '../../utils/postUrls';
import { PostBadges } from '../desktop/PostBadges';
import { canModeratePost } from '../../utils/permissions';
import resolveAvatar from '../../lib/resolveAvatar';
import { MobileSiteThemeCustomizer } from './MobileThemeBottomSheet';
import { SubPanelSwiper } from './Navigation';
import { MobilePostOverlay } from './MobilePostOverlay';
import { NavigateWidget, FriendsWidget, JoinedCommunitiesWidget, DiscoverCommunitiesWidget } from '../common/widgets';
import { useTheme } from '../../context/ThemeContext';

import { fetchWithAuth } from '../../lib/fetchWithAuth';
// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getPostUrl(post: FeedPost): string {
  if (post.slug && post.communitySlug && post.postType === 'forum')
    return getForumPostUrl(post.communitySlug, post.slug);
  if (post.slug && post.communitySlug && post.postType === 'community')
    return getCommunityPostUrl(post.communitySlug, post.slug);
  if (post.slug && post.authorUsername)
    return getLegacyPostUrl(post.authorUsername, post.slug);
  return getLegacyPostUrl(post.authorUsername || 'unknown', post.slug || post.id.toString());
}

function selectPreviewComments(replies: Reply[]): Reply[] {
  if (!replies || replies.length === 0) return [];
  if (replies.length <= 3) return replies;
  const selected: Reply[] = [];
  const available = [...replies];
  const mostPopular = available.reduce((prev, curr) => (curr.likes > prev.likes) ? curr : prev);
  selected.push(mostPopular);
  available.splice(available.indexOf(mostPopular), 1);
  const mostControversial = available.reduce((prev, curr) => {
    const ps = prev.dislikes > 0 ? prev.dislikes : Math.abs(prev.likes - prev.dislikes);
    const cs = curr.dislikes > 0 ? curr.dislikes : Math.abs(curr.likes - curr.dislikes);
    return cs > ps ? curr : prev;
  });
  selected.push(mostControversial);
  available.splice(available.indexOf(mostControversial), 1);
  if (available.length > 0) selected.push(available[Math.floor(Math.random() * available.length)]);
  return selected;
}

const TABS = [
  { id: 'all' as const, label: 'All' },
  { id: 'joined' as const, label: 'Joined' },
  { id: 'popular' as const, label: 'Popular' },
  { id: 'friends' as const, label: 'Friends' },
  { id: 'following' as const, label: 'Following' },
];

const SORT_OPTIONS = [
  { value: 'newest' as const, label: 'Newest', icon: Calendar },
  { value: 'oldest' as const, label: 'Oldest', icon: Clock },
  { value: 'popular' as const, label: 'Popular', icon: TrendingUp },
  { value: 'unpopular' as const, label: 'Unpopular', icon: TrendingDown },
  { value: 'controversial' as const, label: 'Controversial', icon: Flame },
];

// ---------------------------------------------------------------------------
// MobileCommunityList
// ---------------------------------------------------------------------------

export interface MobileCommunityListProps {
  currentUser: {
    id: string;
    username: string;
    displayName?: string;
    avatar?: string;
    isSiteAdmin?: boolean;
    isSuperAdmin?: boolean;
    isSiteModerator?: boolean;
  } | null;
  activePanel: number;
  onPanelChange: (index: number) => void;
  screenWidth: number;
}

export function MobileCommunityList({ currentUser, activePanel, onPanelChange, screenWidth }: MobileCommunityListProps) {
  const data = useCommunityData();
  const currentSortOption = SORT_OPTIONS.find(o => o.value === data.sortBy) || SORT_OPTIONS[0];
  const [showPostModal, setShowPostModal] = useState(false);
  const [joinedCommunities, setJoinedCommunities] = useState<{ id: string; name: string }[]>([]);

  // Fetch user's joined communities for the community selector
  useEffect(() => {
    if (!currentUser) return;
    (async () => {
      try {
        const all = await getCommunities() as any[];
        setJoinedCommunities(
          all.filter((c: any) => c.isMember).map((c: any) => ({ id: String(c.id), name: c.name }))
        );
      } catch { /* silent */ }
    })();
  }, [currentUser]);

  const handlePostSubmit = React.useCallback(async (postData: {
    title: string; content: string; tags: string[]; mediaUrl: string; mediaPosition: 'above' | 'below';
    communityId?: string; postType?: 'community' | 'forum'; visibility?: 'public' | 'members-only';
  }) => {
    try {
      if (postData.communityId) {
        // Post to community
        const community = joinedCommunities.find(c => c.id === postData.communityId);
        if (!community) return;
        // Need the slug — fetch it or derive from name
        const allComms = await getCommunities() as any[];
        const fullComm = allComms.find((c: any) => String(c.id) === postData.communityId);
        if (!fullComm) return;
        const res = await fetchWithAuth(`/api/communities/${fullComm.nameSlug || fullComm.name.toLowerCase().replace(/\s+/g, '-')}/posts`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            title: postData.title.trim() || undefined,
            content: postData.content,
            tags: postData.tags,
            postType: postData.postType || 'community',
            mediaUrl: postData.mediaUrl || undefined,
            mediaPosition: postData.mediaPosition,
            visibility: postData.visibility || 'public',
          }),
        });
        if (res.ok) setShowPostModal(false);
      } else {
        // Post to personal feed
        const res = await fetchWithAuth('/api/posts', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(postData),
        });
        if (res.ok) setShowPostModal(false);
      }
    } catch { /* silent */ }
  }, [joinedCommunities]);

  return (
    <>
      <SubPanelSwiper activePanel={activePanel} onPanelChange={onPanelChange} screenWidth={screenWidth}>
        {[
          <LeftPanel key="left" currentUser={currentUser} onShareClick={() => setShowPostModal(true)} />,
          <CenterPanel key="center" data={data} currentUser={currentUser} currentSortOption={currentSortOption} />,
          <RightPanel key="right" currentUser={currentUser} />,
        ]}
      </SubPanelSwiper>

      {/* Post creation overlay — rendered outside SubPanelSwiper via portal */}
      <MobilePostOverlay
        isOpen={showPostModal}
        onClose={() => setShowPostModal(false)}
        onSubmit={handlePostSubmit}
        currentUser={currentUser}
        heading="Create Community Post"
        communities={joinedCommunities}
        showPostTypeSelector={true}
        showVisibilitySelector={true}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// Left Panel — Share + Create Community
// ---------------------------------------------------------------------------

function LeftPanel({ currentUser, onShareClick }: { currentUser: MobileCommunityListProps['currentUser']; onShareClick: () => void }) {
  const [isShareAnimating, setIsShareAnimating] = useState(false);

  return (
    <div className="p-4 flex flex-col items-center gap-3" style={{ paddingTop: 'max(1.5rem, env(safe-area-inset-top) + 1rem)' }}>
      {currentUser && (
        <button
          onClick={() => {
            setIsShareAnimating(true);
            setTimeout(() => { onShareClick(); setIsShareAnimating(false); }, 300);
          }}
          className="rounded-lg shadow-lg font-semibold overflow-hidden"
          style={{ backgroundColor: 'var(--color-accent)', color: 'var(--text-color)', padding: '16px 48px', fontSize: '18px' }}
        >
          <span style={{
            display: 'inline-block',
            transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
            opacity: isShareAnimating ? 0 : 1,
            transform: isShareAnimating ? 'translateX(-20px)' : 'translateX(0)',
          }}>
            Share
          </span>
        </button>
      )}

      {/* Create Community (mods/admins only) */}
      {currentUser && (currentUser.isSiteModerator || currentUser.isSiteAdmin || currentUser.isSuperAdmin) && (
        <button
          className="rounded-lg shadow-lg font-semibold whitespace-nowrap"
          style={{ backgroundColor: 'var(--color-accent)', color: 'var(--text-color)', padding: '16px 48px', fontSize: '18px' }}
        >
          Create Community
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Center Panel — Tabs + Sort + Community Posts
// ---------------------------------------------------------------------------

function CenterPanel({
  data,
  currentUser,
  currentSortOption,
}: {
  data: ReturnType<typeof useCommunityData>;
  currentUser: MobileCommunityListProps['currentUser'];
  currentSortOption: typeof SORT_OPTIONS[number];
}) {
  const navigate = useNavigate();
  const [showSortMenu, setShowSortMenu] = useState(false);
  const [showModerateMenu, setShowModerateMenu] = useState<string | null>(null);

  return (
    <div className="flex flex-col items-center w-full px-2 h-full overflow-y-auto overscroll-contain" style={{ WebkitOverflowScrolling: 'touch' }}>
      {/* Tab bar */}
      <div className="rounded-lg border mb-3 w-full" style={{
        background: 'var(--tab-bar-bg, rgba(30, 58, 95, 0.95))',
        borderColor: 'rgba(var(--accent-rgb, 6,182,212), 0.25)',
        padding: '0 12px',
      }}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 overflow-x-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
            {TABS.map(tab => (
              <button
                key={tab.id}
                onClick={() => data.setActiveTab(tab.id)}
                className="font-semibold whitespace-nowrap"
                style={{
                  color: data.activeTab === tab.id ? 'var(--text-color)' : 'var(--text-color-muted, rgba(255,255,255,0.6))',
                  borderBottom: data.activeTab === tab.id ? '2px solid var(--color-accent)' : '2px solid transparent',
                  paddingBottom: '4px',
                  paddingTop: '8px',
                  fontSize: '13px',
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Sort */}
          <div className="relative" style={{ zIndex: 41 }}>
            <button onClick={() => setShowSortMenu(p => !p)} className="flex items-center rounded-lg font-medium" style={{ gap: '4px', padding: '4px 8px', color: 'var(--text-color)', fontSize: '12px' }}>
              <currentSortOption.icon size={14} />
              <ChevronDown size={14} className={showSortMenu ? 'rotate-180' : ''} style={{ transition: 'transform 0.2s' }} />
            </button>
            {showSortMenu && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setShowSortMenu(false)} />
                <div className="absolute top-full right-0 rounded-lg overflow-hidden shadow-2xl z-50" style={{ background: 'var(--card-bg)', border: '1px solid rgba(var(--accent-rgb, 6,182,212), 0.25)', marginTop: '4px', minWidth: '140px' }}>
                  {SORT_OPTIONS.map(option => (
                    <button key={option.value} onClick={() => { data.setSortBy(option.value); setShowSortMenu(false); }} className="flex items-center gap-2 w-full text-left" style={{ padding: '10px 14px', backgroundColor: data.sortBy === option.value ? 'rgba(var(--accent-rgb, 6,182,212), 0.2)' : 'transparent', color: 'var(--text-color)', fontSize: '13px' }}>
                      <option.icon size={14} style={{ opacity: 0.7 }} />
                      {option.label}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Wavy divider */}
      <div className="w-full" style={{ marginTop: '12px', marginBottom: '16px' }}>
        <svg width="100%" height="12" viewBox="0 0 1000 20" preserveAspectRatio="none" style={{ display: 'block' }}>
          <path d="M0,10 Q25,0 50,10 T100,10 T150,10 T200,10 T250,10 T300,10 T350,10 T400,10 T450,10 T500,10 T550,10 T600,10 T650,10 T700,10 T750,10 T800,10 T850,10 T900,10 T950,10 T1000,10" fill="none" stroke="var(--color-accent)" strokeWidth="2" opacity="0.4" />
        </svg>
      </div>

      {/* Loading / Empty / Posts */}
      {data.isLoading ? (
        <div className="flex items-center justify-center py-16">
          <div className="w-6 h-6 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: 'var(--color-accent)', borderTopColor: 'transparent' }} />
        </div>
      ) : data.posts.length === 0 ? (
        <div className="text-center py-16">
          <Globe size={32} style={{ color: 'var(--text-color)', opacity: 0.25, margin: '0 auto 12px' }} />
          <h2 className="text-xl font-bold mb-3" style={{ color: 'var(--text-color)' }}>No community posts</h2>
          <p className="text-sm" style={{ color: 'var(--text-color)', opacity: 0.6 }}>Join communities to see posts here</p>
        </div>
      ) : (
        <div className="w-full flex flex-col items-center">
          {data.posts.map(post => (
            <CommunityPostCard
              key={post.id}
              post={post}
              currentUser={currentUser}
              showModerateMenu={showModerateMenu}
              setShowModerateMenu={setShowModerateMenu}
              onLike={data.likePost}
              onDislike={data.dislikePost}
              onDelete={data.deletePost}
              navigate={navigate}
            />
          ))}
        </div>
      )}

      <div className="h-24 shrink-0" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Right Panel — Community discovery
// ---------------------------------------------------------------------------

const WIDGET_STYLE: React.CSSProperties = {
  backgroundColor: 'var(--card-bg)',
  border: '1px solid rgba(var(--accent-rgb, 6,182,212), 0.25)',
  borderRadius: 8,
};

function RightPanel({ currentUser }: { currentUser: MobileCommunityListProps['currentUser'] }) {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [showThemeCustomizer, setShowThemeCustomizer] = useState(false);

  return (
    <div className="pt-4 px-4 flex flex-col gap-4 h-full overflow-y-auto overscroll-contain" style={{ WebkitOverflowScrolling: 'touch' }}>
      {currentUser && (
        <button
          onClick={() => setShowThemeCustomizer(true)}
          className="rounded-lg font-semibold w-full flex items-center justify-center gap-2"
          style={{ backgroundColor: 'var(--color-accent)', color: 'var(--text-color)', padding: '0.75rem 1rem', fontSize: '0.875rem', minHeight: '48px' }}
        >
          Customize Theme
        </button>
      )}

      {/* Navigate Widget */}
      <NavigateWidget theme={theme} compact showHeader onNavigate={(path) => navigate(path)} style={WIDGET_STYLE} />

      {/* Joined Communities Widget */}
      <JoinedCommunitiesWidget theme={theme} compact showHeader onNavigate={(path) => navigate(path)} style={WIDGET_STYLE} />

      {/* Discover Communities Widget */}
      <DiscoverCommunitiesWidget theme={theme} compact showHeader onNavigate={(path) => navigate(path)} style={WIDGET_STYLE} />

      {/* Friends Widget */}
      <FriendsWidget theme={theme} maxDisplay={8} compact showHeader onNavigate={(path) => navigate(path)} style={WIDGET_STYLE} />

      <div className="h-24 shrink-0" />

      {currentUser && (
        <MobileSiteThemeCustomizer isOpen={showThemeCustomizer} onClose={() => setShowThemeCustomizer(false)} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Community Post Card
// ---------------------------------------------------------------------------

function CommunityPostCard({
  post,
  currentUser,
  showModerateMenu,
  setShowModerateMenu,
  onLike,
  onDislike,
  onDelete,
  navigate,
}: {
  post: FeedPost;
  currentUser: MobileCommunityListProps['currentUser'];
  showModerateMenu: string | null;
  setShowModerateMenu: (id: string | null) => void;
  onLike: (id: string) => void;
  onDislike: (id: string) => void;
  onDelete: (id: string) => void;
  navigate: (path: string) => void;
}) {
  const canModerate = currentUser && canModeratePost(post as any, {
    id: parseInt(currentUser.id),
    username: currentUser.username,
    isAdmin: currentUser.isSiteAdmin || currentUser.isSuperAdmin || false,
    isModerator: currentUser.isSiteModerator || false,
  });

  return (
    <div className="rounded-lg p-4 border relative w-full" style={{
      background: 'var(--card-bg)',
      borderColor: 'rgba(var(--accent-rgb, 6,182,212), 0.25)',
      marginBottom: '1rem',
    }}>
      <PostBadges post={post as any} textColor="var(--text-color)" accentColor="var(--color-accent)" />

      {canModerate && (
        <div className="absolute top-3 right-3" style={{ zIndex: 10 }}>
          <button onClick={() => setShowModerateMenu(showModerateMenu === post.id ? null : post.id)} className="p-1.5 rounded-lg" style={{ backgroundColor: 'rgba(var(--accent-rgb, 6,182,212), 0.12)', color: 'var(--text-color)' }}>
            <MoreVertical size={16} />
          </button>
          {showModerateMenu === post.id && (
            <>
              <div className="absolute top-full right-0 mt-2 rounded-lg overflow-hidden shadow-2xl min-w-[140px]" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid rgba(var(--accent-rgb, 6,182,212), 0.4)', zIndex: 1001 }}>
                <button onClick={() => { if (confirm('Delete?')) { onDelete(post.id); setShowModerateMenu(null); } }} className="flex items-center gap-2 w-full px-4 py-3 text-left text-sm font-medium" style={{ color: '#ef4444' }}>
                  <Trash size={16} /> Delete
                </button>
              </div>
              <div className="fixed inset-0 z-40" onClick={() => setShowModerateMenu(null)} />
            </>
          )}
        </div>
      )}

      {/* Author */}
      <div className="flex items-start gap-3 mb-3">
        <Link to={post.authorUsername ? `/user/${post.authorUsername}` : '#'} className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'var(--color-accent)' }}>
          {post.authorAvatar ? (
            <img src={post.authorAvatar} alt="" className="w-full h-full rounded-full object-cover" />
          ) : (
            <span className="text-sm font-bold" style={{ color: 'var(--app-bg)' }}>{post.author.charAt(0).toUpperCase()}</span>
          )}
        </Link>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold truncate" style={{ color: 'var(--text-color)' }}>{post.author}</h3>
            {post.communityName && <span className="text-xs flex-shrink-0" style={{ color: 'var(--text-color)', opacity: 0.6 }}>in {post.communityName}</span>}
            <span className="text-xs flex-shrink-0" style={{ color: 'var(--text-color)', opacity: 0.6 }}>{post.timestamp}</span>
          </div>
          {post.authorUsername && <span className="text-xs" style={{ color: 'var(--text-color)', opacity: 0.6 }}>@{post.authorUsername}</span>}
        </div>
      </div>

      {/* Moderation disclaimer */}
      {post.editedByModeratorId && (
        <div className="mb-3 px-3 py-2 rounded border" style={{ backgroundColor: '#854d0e20', borderColor: '#ca8a0480' }}>
          <div className="flex items-center gap-2">
            <AlertCircle size={14} className="text-yellow-400" />
            <p className="text-xs text-yellow-100">Edited by a moderator</p>
          </div>
        </div>
      )}

      {/* Content */}
      <Link to={getPostUrl(post)} className="block">
        {post.title && <h2 className="text-base font-bold mb-2" style={{ color: 'var(--text-color)' }}>{post.title}</h2>}
        {post.aboveContent && post.mediaUrl && (
          <div className="mb-3 rounded-lg overflow-hidden">
            <img src={post.mediaUrl} alt="" className="w-full h-auto" style={{ maxHeight: '300px', objectFit: 'cover' }} loading="lazy" />
          </div>
        )}
        <p className="text-sm leading-relaxed mb-3 whitespace-pre-wrap" style={{ color: 'var(--text-color)' }}>{post.content}</p>
        {post.belowContent && post.mediaUrl && (
          <div className="mt-3 rounded-lg overflow-hidden">
            <img src={post.mediaUrl} alt="" className="w-full h-auto" style={{ maxHeight: '300px', objectFit: 'cover' }} loading="lazy" />
          </div>
        )}
      </Link>

      {/* Tags */}
      {post.tags && post.tags.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3">
          {post.tags.map((tag, idx) => (
            <span key={idx} className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs" style={{ backgroundColor: 'rgba(var(--accent-rgb, 6,182,212), 0.2)', color: 'var(--text-color)' }}>
              <Hash size={10} />{tag}
            </span>
          ))}
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => onLike(post.id)} className="flex items-center gap-1.5" style={{ color: post.liked ? '#f87171' : 'var(--text-color)', opacity: post.liked ? 1 : 0.6, minHeight: '44px' }}>
            <Heart size={16} className={post.liked ? 'fill-current' : ''} /><span className="text-xs">{post.likes}</span>
          </button>
          <button onClick={() => onDislike(post.id)} className="flex items-center gap-1.5" style={{ color: post.disliked ? '#f97316' : 'var(--text-color)', opacity: post.disliked ? 1 : 0.6, minHeight: '44px' }}>
            <ThumbsDown size={16} className={post.disliked ? 'fill-current' : ''} /><span className="text-xs">{post.dislikes}</span>
          </button>
          <button onClick={() => navigate(getPostUrl(post))} className="flex items-center gap-1.5" style={{ color: 'var(--text-color)', opacity: 0.6, minHeight: '44px' }}>
            <MessageSquare size={16} /><span className="text-xs">{post.comments}</span>
          </button>
          <button className="flex items-center gap-1.5" style={{ color: 'var(--text-color)', opacity: 0.6, minHeight: '44px' }}>
            <Share2 size={16} />
          </button>
        </div>
        <button style={{ color: 'var(--text-color)', opacity: 0.6 }}><Bookmark size={16} /></button>
      </div>

      {/* Comment preview */}
      {post.replies && post.replies.length > 0 && (() => {
        const previews = selectPreviewComments(post.replies);
        return (
          <div className="mt-4 pt-4" style={{ borderTop: '1px solid rgba(var(--accent-rgb, 6,182,212), 0.12)' }}>
            <div className="space-y-3">
              {previews.map(reply => (
                <div key={reply.id} className="flex gap-3">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'var(--color-accent)' }}>
                    {reply.author.profilePicture ? (
                      <img src={resolveAvatar(reply.author.profilePicture)} alt="" className="w-full h-full rounded-full object-cover" />
                    ) : (
                      <span className="text-xs font-bold" style={{ color: 'var(--app-bg)' }}>{(reply.author.profileName || reply.author.username).charAt(0).toUpperCase()}</span>
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-semibold" style={{ color: 'var(--text-color)' }}>{reply.author.profileName || reply.author.username}</span>
                      <span className="text-xs" style={{ color: 'var(--text-color)', opacity: 0.4 }}>{new Date(reply.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                    </div>
                    <p className="text-xs" style={{ color: 'var(--text-color)', opacity: 0.8 }}>{reply.content}</p>
                  </div>
                </div>
              ))}
            </div>
            {post.comments > 3 && (
              <button className="text-xs mt-2" style={{ color: 'var(--color-accent)' }} onClick={() => navigate(getPostUrl(post))}>
                View all {post.comments} comments
              </button>
            )}
          </div>
        );
      })()}
    </div>
  );
}
